import { useState, FC, useEffect } from 'react'
import {
  Grid,
  Table,
  TableCell,
  TableRow,
  Switch,
  Typography,
  TextField,
  Autocomplete,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../../hooks'
import { getUserProfileDataAction } from '../../../../store/users'
import { UpdateSettingsRequest } from 'src/services'
import CustomNumberField from '../profile/CustomNumberField'

export interface AccountSettingsProps {
  onChange: (newSettings: UpdateSettingsRequest) => void
  invalidEmail: boolean
  isOnboarding?: boolean
}

const AccountSettings: FC<AccountSettingsProps> = ({
  isOnboarding,
  onChange,
  invalidEmail,
}) => {
  const [language, setLanguage] = useState<{ label: string; value: 'en' | 'hi' | 'pa' }>({ label: 'English', value: 'en' })
  // Existing State Variables
  const [isDriveBackUpEnabled, setDriveBackupEnabled] = useState<boolean>(false)
  const [is30DaysMonthEnabled, set30DaysMonthEnabled] = useState<boolean>(false)
  const [isCycleBillHelperEnabled, setCycleBillHelperEnabled] =
    useState<boolean>(false)
  const [enabledChallanBilling, setEnabledChallanBilling] = useState<boolean>(false)
  const [isOtherStoreEnabled, setOtherStoreEnabled] = useState<boolean>(false)
  const [isGSTEnabled, setGSTEnabled] = useState<boolean>(false)
  const [isRemainingDayDiscountEnabled, setRemainingDayDiscountEnabled] =
    useState<boolean>(false)
  const [is3InchPrinter, set3InchPrinter] = useState<boolean>(false)
  const [isMixTypeAllowed, setMixTypeAllowed] = useState<boolean>(true)
  const [isReoccuringBillsOn, setReoccuringBillsOn] = useState<boolean>(false)
  const [isMinDaysEnabled, setMinDaysEnabled] = useState<boolean>(false)
  const [billFormat, setBillFormat] = useState('FORMAT_1')
  const [backupEmail, setBackupEmail] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<boolean | null>(null)
  const { data } = useAppSelector((state) => state.userData)
  const dispatchAction = useAppDispatch()
  const tabs = [
    { value: 'edit_profile', label: 'Edit Profile' },
    { value: 'security', label: 'Passwords/Security' },
  ]

  useEffect(() => {
    dispatchAction(getUserProfileDataAction())
  }, [dispatchAction])

  useEffect(() => {
    setEmailError(invalidEmail)
  }, [invalidEmail])


  useEffect(() => {
    const request: UpdateSettingsRequest = {
      id: data?.config?.id,
      isOtherStoreEnabled: isOtherStoreEnabled,
      isGSTEnabled: isGSTEnabled,
      isRemainingDayDiscountEnabled: isRemainingDayDiscountEnabled,
      is3InchPrinter: is3InchPrinter,
      isMinDaysEnabled: isMinDaysEnabled,
      isMixTypeAllowed: isOnboarding ? true : isMixTypeAllowed,
      billFormat: billFormat,
      isRecurringBillsEnabled: isReoccuringBillsOn,
      backupEmail: backupEmail,
      is30DaysMonthEnabled: is30DaysMonthEnabled,
      isCycleBillHelperEnabled: isCycleBillHelperEnabled,
      // Preserve existing values for moved fields (now managed in Invoice & Challan Settings)
      sgst: data?.config?.sgst || 9,
      cgst: data?.config?.cgst || 9,
      upiId: data?.config?.upiId,
      upiId2: data?.config?.upiId2,
      outwardChallanPrefix: data?.config?.outwardChallanPrefix,
      inwardChallanPrefix: data?.config?.inwardChallanPrefix,
      invoiceNoPrefix: data?.config?.invoiceNoPrefix,
      enabledChallanBilling: enabledChallanBilling,
      pdfLanguage: language?.value
    }
    onChange(request)
  }, [
    data,
    isDriveBackUpEnabled,
    isOtherStoreEnabled,
    isGSTEnabled,
    isRemainingDayDiscountEnabled,
    is3InchPrinter,
    isMinDaysEnabled,
    isMixTypeAllowed,
    billFormat,
    isReoccuringBillsOn,
    backupEmail,
    is30DaysMonthEnabled,
    isCycleBillHelperEnabled,
    enabledChallanBilling,
    language,
    isOnboarding
  ])

  useEffect(() => {
    if (data && data.config) {
      setOtherStoreEnabled(data.config.isOtherStoreEnabled)
      setGSTEnabled(data.config.isGSTEnabled)
      setRemainingDayDiscountEnabled(data.config.isRemainingDayDiscountEnabled)
      set3InchPrinter(data.config.is3InchPrinter)
      setMinDaysEnabled(data.config.isMinDaysEnabled)
      setMixTypeAllowed(isOnboarding ? true : data.config.isMixTypeAllowed)
      setReoccuringBillsOn(data.config.isRecurringBillsEnabled)
      set30DaysMonthEnabled(data.config.is30DaysMonthEnabled)
      setCycleBillHelperEnabled(!!data.config.isCycleBillHelperEnabled)
      setEnabledChallanBilling(data.config.enabledChallanBilling)
      setBillFormat(data.config.billFormat ? data.config.billFormat : 'FORMAT_1')
      setBackupEmail(data.config.backupEmail)
      if(data.config?.pdfLanguage === "pa") {
        setLanguage({label: "Punjabi", value: "pa"})
      } else if(data.config?.pdfLanguage === "hi") {
        setLanguage({label: "Hindi", value: "hi"})
      } else {
        setLanguage({label: "English", value: "en"})
      }
    }
  }, [data, isOnboarding])

  return (
    <>
      <Table>
        {/* Other Existing Rows (Commented Out Row Removed for Clarity) */}
        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Grid container direction='column'>
              <Grid item xs>
                <Typography variant="h5">
                  Enable Monthly/Fortnightly/Weekly Bills
                </Typography>
              </Grid>
              <Grid item xs>
                Please turn on this switch if you generate bill on same account
                after regular interval, such as on every month, every week etc.
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right">
            <Switch
              checked={isReoccuringBillsOn}
              onChange={() => {
                setReoccuringBillsOn(!isReoccuringBillsOn)
              }}
            />
          </TableCell>
        </TableRow>

        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Grid container direction='column'>
              <Grid item xs>
                <Typography variant="h5">
                  Enable new bill format (Simpler Format)
                </Typography>
              </Grid>
              <Grid item xs>
                You can select from OLD or NEW bill format, you should try both formats to know which is better for you.
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right">
            <Switch
              checked={billFormat === "FORMAT_2"}
              onChange={() => {
                let format = billFormat !== "FORMAT_2" ? "FORMAT_2" : "FORMAT_1"
                setBillFormat(format)
              }}
            />
          </TableCell>
        </TableRow>

        {!isOnboarding && (
          <TableRow sx={{ height: 72 }}>
            <TableCell>
              <Grid container direction='column'>
                <Grid item xs>
                  <Typography variant="h5">Enable MIX type billing</Typography>
                </Grid>
                <Grid item xs>
                  Enable MIX type billing (SQFT and PER ITEM billing in same
                  account)
                </Grid>
              </Grid>
            </TableCell>
            <TableCell align="right">
              <Switch
                id="mix_bill_switch"
                checked={isMixTypeAllowed}
                onChange={() => {
                  setMixTypeAllowed(!isMixTypeAllowed)
                }}
              />
            </TableCell>
          </TableRow>
        )}

        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Grid container direction='column'>
              <Grid item xs>
                <Typography variant="h5">
                  3 inch thermal printer(For mobile)
                </Typography>
              </Grid>
              <Grid item xs>
                Turn on if you use 3 inch thermal printer
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right">
            <Switch
              checked={is3InchPrinter}
              onChange={() => {
                set3InchPrinter(!is3InchPrinter)
              }}
            />
          </TableCell>
        </TableRow>

        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Grid container direction='column'>
              <Grid item xs>
                <Typography variant="h5">Day discount on left items</Typography>
              </Grid>
              <Grid item xs>
                Enable one day discount on left items{' '}
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right">
            <Switch
              checked={isRemainingDayDiscountEnabled}
              onChange={() => {
                setRemainingDayDiscountEnabled(!isRemainingDayDiscountEnabled)
              }}
            />
          </TableCell>
        </TableRow>

        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Grid container direction='column'>
              <Grid item xs>
                <Typography variant="h5">Other store feature</Typography>
              </Grid>
              <Grid item xs>
                Enable feature to borrow items from other stores
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right">
            <Switch
              checked={isOtherStoreEnabled}
              onChange={() => {
                setOtherStoreEnabled(!isOtherStoreEnabled)
              }}
            />
          </TableCell>
        </TableRow>

        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Grid container direction='column'>
              <Grid item xs>
                <Typography variant="h5">Minimum days feature</Typography>
              </Grid>
              <Grid item xs>
                Enable minimum days feature (example rent for min 10 days, 15
                days)
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right">
            <Switch
              checked={isMinDaysEnabled}
              onChange={() => {
                setMinDaysEnabled(!isMinDaysEnabled)
              }}
            />
          </TableCell>
        </TableRow>

        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Grid container direction='column'>
              <Grid item xs>
                <Typography variant="h5">Enable 30 days month feature</Typography>
              </Grid>
              <Grid item xs>
                If you turn this on then while counting the days every month will be considered as 30 days month and there will 360 days in a year.
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right">
            <Switch
              checked={is30DaysMonthEnabled}
              onChange={() => {
                set30DaysMonthEnabled(!is30DaysMonthEnabled)
              }}
            />
          </TableCell>
        </TableRow>

        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Grid container direction='column'>
              <Grid item xs>
                <Typography variant="h5">
                  Bill Only Completed Monthly Cycles
                </Typography>
              </Grid>
              <Grid item xs>
                Include rental items in an invoice only when their monthly
                billing cycle ends within the selected invoice date range.
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right">
            <Switch
              checked={isCycleBillHelperEnabled}
              onChange={() => {
                setCycleBillHelperEnabled(!isCycleBillHelperEnabled)
              }}
            />
          </TableCell>
        </TableRow>

        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Grid container direction='column'>
              <Grid item xs>
                <Typography variant="h5">Enable advance invoicing</Typography>
              </Grid>
              <Grid item xs>
                If generate invoices while issuing the items, then turn this on.
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right">
            <Switch
              checked={enabledChallanBilling}
              onChange={() => {
                setEnabledChallanBilling(!enabledChallanBilling)
              }}
            />
          </TableCell>
        </TableRow>

        {/* Select language for Invoices, Challans PDF - Second last */}
        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Grid container direction='column'>
              <Grid item xs>
                <Typography variant="h5" color={'#9400D3'}>
                  Select language for Invoices, Challans PDF
                </Typography>
              </Grid>
              <Grid item xs>
                Your web will remain in english but invoices, challans will be in selected language.
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="right">
          <Autocomplete
          size="small"
          disablePortal
          id="language-autocomplete"
          value={language}
          options={[
            { label: 'English', value: 'en' },
            { label: 'Hindi', value: 'hi' },
            { label: 'Punjabi', value: 'pa' },
          ]}
          onChange={(e, newValue) => {
            if (
              newValue &&
              (newValue.value === 'en' || newValue.value === 'hi' || newValue.value === 'pa')
            ) {
              setLanguage(newValue as { label: string; value: 'en' | 'hi' | 'pa' })
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Language"
              fullWidth
            />
          )}
        />
          </TableCell>
        </TableRow>

        {/* Backup Email - Last position */}
        <TableRow sx={{ height: 72 }}>
          <TableCell>
            <Typography variant="h5">
              Enter daily backup email address
            </Typography>
          </TableCell>
          <TableCell align="right">
            <TextField
              size='small'
              value={backupEmail ?? ""}
              onChange={(e) => {
                setEmailError(false)
                setBackupEmail(e.target.value)
              }}
              label="Backup email"
              sx={{ width: 300 }}
              error={!!emailError}
              helperText={emailError ? "Invalid email address" : ""}
            />
          </TableCell>
        </TableRow>
      </Table>
    </>
  )
}

export default AccountSettings
